import { AnimatePresence, motion } from 'motion/react'
import type { CustomerOrder } from '../../customerOrdersApi'
import type { ShopLanguage, ShopLocale, ShopMessageKey } from '../../../i18n'
import {
  CustomerAreaSurfaceCard,
  EmptyStateBlock,
  customerMotionTransition,
} from '../customer-area'
import {
  OrderProgressStep,
  PackageIcon,
  TrackingPanelSkeleton,
} from './dashboardShared'
import { DashboardTrackingSelectedOrderCard } from './DashboardTrackingSelectedOrderCard'
import { DashboardTrackingProgress } from './DashboardTrackingProgress'
import { DashboardTrackingActions } from './DashboardTrackingActions'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type DashboardTrackingSectionProps = {
  reducedMotion: boolean
  ordersLoading: boolean
  selectedOrder: CustomerOrder | null
  selectedOrderProgress: OrderProgressStep[]
  selectedOrderNextStep: OrderProgressStep | null
  language: ShopLanguage
  locale: ShopLocale
  isItalian: boolean
  t: TranslateFn
  onOpenOrderDetail: (orderId: string) => void
  onOpenCatalog: () => void
  onRequestSupport: (order: CustomerOrder) => void
}

export const DashboardTrackingSection = ({
  reducedMotion,
  ordersLoading,
  selectedOrder,
  selectedOrderProgress,
  selectedOrderNextStep,
  language,
  locale,
  isItalian,
  t,
  onOpenOrderDetail,
  onOpenCatalog,
  onRequestSupport,
}: DashboardTrackingSectionProps) => (
  <CustomerAreaSurfaceCard
    title={isItalian ? 'Tracking e dettagli' : 'Tracking and details'}
    subtitle={
      selectedOrder
        ? isItalian
          ? `Dettagli ordine ${selectedOrder.orderNumber}.`
          : `Order details for ${selectedOrder.orderNumber}.`
        : isItalian
          ? 'Seleziona un ordine per vedere stato e dettagli.'
          : 'Select an order to view status and details.'
    }
  >
    <AnimatePresence mode="wait" initial={false}>
      {ordersLoading && !selectedOrder ? (
        <motion.div
          key="tracking-loading"
          initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
          animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
          transition={customerMotionTransition}
        >
          <TrackingPanelSkeleton />
        </motion.div>
      ) : null}

      {!ordersLoading && !selectedOrder ? (
        <motion.div
          key="tracking-empty"
          initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
          animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
          transition={customerMotionTransition}
        >
          <EmptyStateBlock
            icon={<PackageIcon className="h-4 w-4" />}
            title={isItalian ? 'Nessun ordine selezionato' : 'No order selected'}
            description={
              isItalian
                ? 'Quando avrai ordini recenti, il dettaglio comparira qui in modo dinamico.'
                : 'When you have recent orders, the dynamic detail panel will appear here.'
            }
          />
        </motion.div>
      ) : null}

      {selectedOrder ? (
        <motion.div
          key={`tracking-order-${selectedOrder.id}-${selectedOrder.status}`}
          initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
          animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
          transition={customerMotionTransition}
          className="space-y-7"
        >
          <DashboardTrackingSelectedOrderCard
            selectedOrder={selectedOrder}
            selectedOrderNextStep={selectedOrderNextStep}
            language={language}
            locale={locale}
            isItalian={isItalian}
            t={t}
          />

          <DashboardTrackingProgress selectedOrderProgress={selectedOrderProgress} reducedMotion={reducedMotion} />

          <DashboardTrackingActions
            selectedOrder={selectedOrder}
            reducedMotion={reducedMotion}
            isItalian={isItalian}
            onOpenOrderDetail={onOpenOrderDetail}
            onOpenCatalog={onOpenCatalog}
            onRequestSupport={onRequestSupport}
          />
        </motion.div>
      ) : null}
    </AnimatePresence>
  </CustomerAreaSurfaceCard>
)
